import React from "react";
import {
  FaWhatsapp,
  FaFacebookF,
  FaYoutube,
  FaXTwitter,
} from "react-icons/fa6";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";

// Official Instagram glyph (Simple Icons / Meta Brand Resources)
const InstagramIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
  >
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

/** Shown when CMS has no URL yet (matches previous hardcoded defaults). */
const FALLBACK_HREF = {
  whatsapp: "https://wa.me/255754360590",
  facebook: "https://www.facebook.com/jrinstitute",
  instagram: "https://www.instagram.com/jrinstituteoftechnology/?hl=en",
  youtube: "https://www.youtube.com/channel/UCM6PtxyjAzWV8UiwbqvkMnw",
  twitter: "https://twitter.com/JriitTechnology/",
};

function pickHref(fromSettings, fallback) {
  if (typeof fromSettings === "string" && fromSettings.trim()) return fromSettings.trim();
  return fallback;
}

const FloatingSocialPanels = () => {
  const s = useSiteSettings();

  const whatsappHref = pickHref(s.whatsappUrl, FALLBACK_HREF.whatsapp);
  const facebookHref = pickHref(s.facebookUrl, FALLBACK_HREF.facebook);
  const instagramHref = pickHref(s.instagramUrl, FALLBACK_HREF.instagram);
  const youtubeHref = pickHref(s.youtubeUrl, FALLBACK_HREF.youtube);
  const twitterHref = pickHref(s.twitterUrl, FALLBACK_HREF.twitter);

  return (
    <div className="hidden md:flex flex-col items-center gap-3 fixed top-1/2 right-0 -translate-y-1/2 z-40 pr-1">
      <div className="flex flex-col gap-2 bg-white/90 backdrop-blur-sm shadow-xl rounded-l-xl pl-3 pr-1 py-2 border border-gray-100">
        <a
          href={whatsappHref}
          target="_blank"
          rel="noreferrer"
          aria-label="WhatsApp"
          className="group relative -mr-6 hover:-mr-1 transition-all duration-300"
          title="Chat on WhatsApp"
        >
          <div className="w-12 h-12 rounded-full bg-[#25D366] text-white shadow-md flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
            <FaWhatsapp className="w-6 h-6" />
          </div>
        </a>
        <a
          href={facebookHref}
          target="_blank"
          rel="noreferrer"
          aria-label="Facebook"
          className="group relative -mr-6 hover:-mr-1 transition-all duration-300"
          title="Facebook"
        >
          <div className="w-12 h-12 rounded-full bg-[#1877F2] text-white shadow-md flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
            <FaFacebookF className="w-5 h-5" />
          </div>
        </a>
        <a
          href={instagramHref}
          target="_blank"
          rel="noreferrer"
          aria-label="Instagram"
          className="group relative -mr-6 hover:-mr-1 transition-all duration-300"
          title="Instagram"
        >
          <div className="w-12 h-12 rounded-full text-white shadow-md flex items-center justify-center transition-transform duration-200 group-hover:scale-105" style={{ background: 'linear-gradient(45deg, #feda75 0%, #fa7e1e 25%, #d62976 50%, #962fbf 75%, #4f5bd5 100%)' }}>
            <InstagramIcon className="w-5 h-5" />
          </div>
        </a>
        <a
          href={youtubeHref}
          target="_blank"
          rel="noreferrer"
          aria-label="YouTube"
          className="group relative -mr-6 hover:-mr-1 transition-all duration-300"
          title="YouTube"
        >
          <div className="w-12 h-12 rounded-full bg-[#FF0000] text-white shadow-md flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
            <FaYoutube className="w-6 h-6" />
          </div>
        </a>
        <a
          href={twitterHref}
          target="_blank"
          rel="noreferrer"
          aria-label="X"
          className="group relative -mr-6 hover:-mr-1 transition-all duration-300"
          title="X"
        >
            <div className="w-12 h-12 rounded-full bg-black text-white shadow-md flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
              <FaXTwitter className="w-5 h-5" />
            </div>
        </a>
      </div>
    </div>
  );
};

export default FloatingSocialPanels;
